import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import {
  partnerOutcomeSnapshotsService,
  outcomeTargetsService,
  quarterlyReviewsService,
  ecityMemberOutcomesService,
  enablementDealCorrelationService
} from '../../../services/enablementOutcomesService';
import { partnerService } from '../../../services/partnerService';
import { useToast } from '../../../contexts/ToastContext';

// ─── Mini Components ────────────────────────────────────────

const ScoreBar = ({ label, before, after, target = null }) => {
  const improvement = (after || 0) - (before || 0);
  const pctAfter = Math.min(after || 0, 100);
  const pctBefore = Math.min(before || 0, 100);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-600">{label}</span>
        <div className="flex items-center gap-8">
          {target && (
            <span className="text-xs text-slate-400">Target: {target}</span>
          )}
          <span className={`text-xs font-bold px-8 py-2 rounded-lg ${improvement > 0 ? 'bg-emerald-100 text-emerald-700' : improvement < 0 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>
            {improvement > 0 ? '+' : ''}{improvement}
          </span>
        </div>
      </div>
      <div className="relative h-3 bg-slate-100 rounded-full overflow-hidden">
        <div className="absolute h-full bg-slate-300 rounded-full" style={{ width: `${pctBefore}%` }} />
        <div className="absolute h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full transition-all" style={{ width: `${pctAfter}%` }} />
        {target && (
          <div className="absolute top-0 h-full w-0.5 bg-amber-500 opacity-80" style={{ left: `${target}%` }} />
        )}
      </div>
      <div className="flex justify-between text-xs text-slate-400">
        <span>Before: {before || 0}</span>
        <span>After: {after || 0}</span>
      </div>
    </div>
  );
};

const AttendanceBadge = ({ status }) => {
  const map = {
    completed: 'bg-emerald-100 text-emerald-700',
    attended: 'bg-blue-100 text-blue-700',
    enrolled: 'bg-slate-100 text-slate-600',
    absent: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`px-8 py-3 rounded-lg text-xs font-semibold capitalize ${map?.[status] || 'bg-slate-100 text-slate-600'}`}>
      {status}
    </span>
  );
};

const TierBadge = ({ tier }) => {
  const map = {
    platinum: 'bg-violet-100 text-violet-700',
    gold: 'bg-amber-100 text-amber-700',
    silver: 'bg-slate-100 text-slate-600',
    bronze: 'bg-orange-100 text-orange-700',
  };
  return (
    <span className={`px-8 py-3 rounded-lg text-xs font-bold uppercase tracking-wide ${map?.[tier] || 'bg-slate-100 text-slate-600'}`}>
      {tier || 'N/A'}
    </span>
  );
};

// ─── Sub-sections ────────────────────────────────────────────

const PartnerOutcomesSection = ({ partners }) => {
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [snapshots, setSnapshots] = useState([]);
  const [correlation, setCorrelation] = useState(null);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (partners?.length > 0 && !selectedPartnerId) {
      setSelectedPartnerId(partners?.[0]?.id);
    }
  }, [partners]);

  useEffect(() => {
    if (selectedPartnerId) loadPartnerData();
  }, [selectedPartnerId]);

  const loadPartnerData = async () => {
    setLoading(true);
    try {
      const [snaps, corr] = await Promise.all([
        partnerOutcomeSnapshotsService?.getByPartner(selectedPartnerId),
        enablementDealCorrelationService?.getByPartner(selectedPartnerId)
      ]);
      setSnapshots(snaps || []);
      setCorrelation(corr?.[0] || null);
    } catch (err) {
      toast?.error?.('Failed to load partner outcomes');
    } finally {
      setLoading(false);
    }
  };

  const baseline = snapshots?.find(s => s?.snapshot_type === 'baseline');
  const latest = snapshots?.filter(s => s?.snapshot_type !== 'baseline')?.slice(-1)?.[0];
  const selectedPartner = partners?.find(p => p?.id === selectedPartnerId);

  const DIMS = [
    { key: 'technical_score', label: 'Technical' },
    { key: 'sales_score', label: 'Sales' },
    { key: 'training_score', label: 'Training' },
    { key: 'delivery_score', label: 'Delivery' },
    { key: 'support_score', label: 'Support' },
  ];

  return (
    <div className="space-y-20">
      {/* Partner Selector */}
      <div className="flex items-center gap-14">
        <label className="text-sm font-semibold text-slate-700 whitespace-nowrap">Select Partner:</label>
        <select
          value={selectedPartnerId}
          onChange={e => setSelectedPartnerId(e?.target?.value)}
          className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 bg-white min-w-[220px]"
        >
          <option value="">— Choose Partner —</option>
          {partners?.map(p => (
            <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>
          ))}
        </select>
        {selectedPartner && <TierBadge tier={selectedPartner?.tier} />}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
        </div>
      ) : !selectedPartnerId ? (
        <div className="bg-white rounded-2xl border border-slate-200/60 p-32 text-center">
          <Icon name="BarChart3" size={36} className="text-slate-300 mx-auto mb-12" />
          <p className="text-slate-500 text-sm">Select a partner to view outcome data</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
          {/* Before/After Capability Scores */}
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-24">
            <div className="flex items-center gap-10 mb-20">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                <Icon name="BarChart3" size={16} className="text-white" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-900">Before / After Capability</h4>
                <p className="text-xs text-slate-500">Baseline vs latest assessment</p>
              </div>
            </div>
            {!baseline && !latest ? (
              <div className="text-center py-16">
                <Icon name="BarChart3" size={28} className="text-slate-300 mx-auto mb-8" />
                <p className="text-slate-400 text-xs">No snapshot data available</p>
              </div>
            ) : (
              <div className="space-y-14">
                {DIMS?.map(dim => (
                  <ScoreBar
                    key={dim?.key}
                    label={dim?.label}
                    before={baseline?.[dim?.key]}
                    after={latest?.[dim?.key]}
                  />
                ))}
                <div className="pt-12 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-600">Overall Score</span>
                  <div className="flex items-center gap-10">
                    <span className="text-xs text-slate-400">{baseline?.overall_score || 0} → </span>
                    <span className="text-lg font-bold text-emerald-600">{latest?.overall_score || 0}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Training Hours + Cert Gains + Deal Correlation */}
          <div className="space-y-16">
            {/* Training Hours & Cert Gains */}
            <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-20">
              <h4 className="text-sm font-bold text-slate-900 mb-16 flex items-center gap-8">
                <Icon name="GraduationCap" size={16} className="text-blue-500" />
                Training & Certification Gains
              </h4>
              <div className="grid grid-cols-2 gap-12">
                <div className="bg-blue-50 rounded-xl p-16 text-center">
                  <p className="text-2xl font-bold text-blue-700">{latest?.training_hours_completed || 0}</p>
                  <p className="text-xs text-blue-600 font-semibold mt-4">Training Hours</p>
                  <p className="text-xs text-slate-400 mt-2">Total completed</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-16 text-center">
                  <p className="text-2xl font-bold text-amber-700">{latest?.certifications_gained || 0}</p>
                  <p className="text-xs text-amber-600 font-semibold mt-4">Certs Gained</p>
                  <p className="text-xs text-slate-400 mt-2">This period</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-16 text-center">
                  <p className="text-2xl font-bold text-emerald-700">{latest?.certifications_active || 0}</p>
                  <p className="text-xs text-emerald-600 font-semibold mt-4">Active Certs</p>
                  <p className="text-xs text-slate-400 mt-2">Currently valid</p>
                </div>
                <div className="bg-violet-50 rounded-xl p-16 text-center">
                  <p className="text-2xl font-bold text-violet-700">{snapshots?.length || 0}</p>
                  <p className="text-xs text-violet-600 font-semibold mt-4">Snapshots</p>
                  <p className="text-xs text-slate-400 mt-2">Tracked periods</p>
                </div>
              </div>
            </div>

            {/* Deal Performance Correlation */}
            <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-20">
              <h4 className="text-sm font-bold text-slate-900 mb-16 flex items-center gap-8">
                <Icon name="TrendingUp" size={16} className="text-emerald-500" />
                Deal Performance Correlation
              </h4>
              {!correlation ? (
                <div className="text-center py-12">
                  <Icon name="TrendingUp" size={24} className="text-slate-300 mx-auto mb-8" />
                  <p className="text-slate-400 text-xs">No correlation data yet</p>
                </div>
              ) : (
                <div className="space-y-10">
                  <div className="flex items-center justify-between py-8 border-b border-slate-100">
                    <span className="text-xs text-slate-500">Win Rate</span>
                    <span className="text-sm font-bold text-emerald-600">{correlation?.win_rate}%</span>
                  </div>
                  <div className="flex items-center justify-between py-8 border-b border-slate-100">
                    <span className="text-xs text-slate-500">Deals Closed</span>
                    <span className="text-sm font-bold text-slate-900">{correlation?.deals_closed}</span>
                  </div>
                  <div className="flex items-center justify-between py-8 border-b border-slate-100">
                    <span className="text-xs text-slate-500">Avg Deal Size</span>
                    <span className="text-sm font-bold text-slate-900">${(correlation?.avg_deal_size || 0)?.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between py-8">
                    <span className="text-xs text-slate-500">Total Revenue</span>
                    <span className="text-sm font-bold text-blue-600">${(correlation?.total_revenue || 0)?.toLocaleString()}</span>
                  </div>
                  {correlation?.correlation_notes && (
                    <p className="text-xs text-slate-400 pt-8 border-t border-slate-100">{correlation?.correlation_notes}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const OutcomeTargetsSection = ({ targets }) => {
  const TIERS = ['platinum', 'gold', 'silver', 'bronze'];
  const tierColors = {
    platinum: 'from-violet-500 to-purple-600',
    gold: 'from-amber-400 to-orange-500',
    silver: 'from-slate-400 to-slate-600',
    bronze: 'from-orange-400 to-red-500',
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-16">
      {TIERS?.map(tier => {
        const t = targets?.find(x => x?.tier === tier);
        return (
          <div key={tier} className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
            <div className={`bg-gradient-to-r ${tierColors?.[tier]} p-16`}>
              <h4 className="text-sm font-bold text-white capitalize">{tier} Tier Targets</h4>
              <p className="text-xs text-white/70 mt-2">{t?.target_year || new Date()?.getFullYear()}</p>
            </div>
            {!t ? (
              <div className="p-16 text-center">
                <p className="text-xs text-slate-400">No targets set</p>
              </div>
            ) : (
              <div className="p-16 space-y-8">
                {[
                  { label: 'Overall Score', value: t?.overall_score_target, unit: '/100' },
                  { label: 'Technical', value: t?.technical_score_target, unit: '/100' },
                  { label: 'Sales', value: t?.sales_score_target, unit: '/100' },
                  { label: 'Training Hrs', value: t?.training_hours_target, unit: 'h' },
                  { label: 'Certifications', value: t?.certifications_target, unit: '' },
                  { label: 'Win Rate', value: t?.deal_win_rate_target, unit: '%' },
                ]?.map(item => (
                  <div key={item?.label} className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">{item?.label}</span>
                    <span className="text-xs font-bold text-slate-900">{item?.value}{item?.unit}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const QuarterlyReviewCards = ({ reviews, partners }) => {
  if (reviews?.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200/60 p-32 text-center">
        <Icon name="CalendarCheck" size={36} className="text-slate-300 mx-auto mb-12" />
        <p className="text-slate-500 text-sm">No quarterly reviews yet</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-16">
      {reviews?.map(review => {
        const partner = partners?.find(p => p?.id === review?.partner_id);
        return (
          <div key={review?.id} className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between px-20 py-14 border-b border-slate-100 bg-slate-50/50">
              <div>
                <p className="text-sm font-bold text-slate-900">{partner?.brand_name || partner?.legal_name || review?.partners?.brand_name || 'Partner'}</p>
                <p className="text-xs text-slate-500 mt-1">{review?.quarter}</p>
              </div>
              <div className="flex items-center gap-8">
                {review?.target_met ? (
                  <span className="flex items-center gap-4 px-10 py-4 bg-emerald-100 text-emerald-700 rounded-lg text-xs font-bold">
                    <Icon name="CheckCircle" size={12} />Target Met
                  </span>
                ) : (
                  <span className="flex items-center gap-4 px-10 py-4 bg-red-100 text-red-700 rounded-lg text-xs font-bold">
                    <Icon name="XCircle" size={12} />Below Target
                  </span>
                )}
              </div>
            </div>
            <div className="p-20 space-y-12">
              <div className="grid grid-cols-3 gap-10">
                <div className="text-center">
                  <p className="text-xl font-bold text-slate-900">{review?.trainings_completed}</p>
                  <p className="text-xs text-slate-400 mt-2">Trainings</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-slate-900">{review?.certs_active}</p>
                  <p className="text-xs text-slate-400 mt-2">Active Certs</p>
                </div>
                <div className="text-center">
                  <p className={`text-xl font-bold ${review?.overall_capability_score >= 76 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {review?.overall_capability_score}
                  </p>
                  <p className="text-xs text-slate-400 mt-2">Score</p>
                </div>
              </div>
              {review?.highlights && (
                <div className="bg-emerald-50 rounded-xl p-12">
                  <p className="text-xs font-semibold text-emerald-700 mb-4 flex items-center gap-6">
                    <Icon name="Star" size={11} />Highlights
                  </p>
                  <p className="text-xs text-emerald-800 leading-relaxed">{review?.highlights}</p>
                </div>
              )}
              {review?.improvement_areas && (
                <div className="bg-amber-50 rounded-xl p-12">
                  <p className="text-xs font-semibold text-amber-700 mb-4 flex items-center gap-6">
                    <Icon name="AlertTriangle" size={11} />Improvement Areas
                  </p>
                  <p className="text-xs text-amber-800 leading-relaxed">{review?.improvement_areas}</p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const MemberOutcomesSection = ({ outcomes, loading }) => {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const filtered = outcomes?.filter(o => {
    const matchSearch = !search || o?.member_name?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = filterStatus === 'all' || o?.attendance_status === filterStatus;
    return matchSearch && matchStatus;
  });

  const avgImprovement = outcomes?.filter(o => o?.score_improvement != null)
    ?.reduce((s, o, _, arr) => s + (o?.score_improvement || 0) / arr?.length, 0);

  return (
    <div className="space-y-16">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-12">
        {[
          { label: 'Total Members', value: outcomes?.length, icon: 'Users', color: 'text-blue-600 bg-blue-50' },
          { label: 'Completed', value: outcomes?.filter(o => o?.attendance_status === 'completed')?.length, icon: 'CheckCircle', color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Certs Earned', value: outcomes?.filter(o => o?.certification_earned)?.length, icon: 'Award', color: 'text-amber-600 bg-amber-50' },
          { label: 'Avg Improvement', value: `+${Math.round(avgImprovement || 0)} pts`, icon: 'TrendingUp', color: 'text-violet-600 bg-violet-50' },
        ]?.map(stat => (
          <div key={stat?.label} className="bg-white rounded-xl border border-slate-200/60 p-16 flex items-center gap-12">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${stat?.color}`}>
              <Icon name={stat?.icon} size={18} />
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900">{stat?.value}</p>
              <p className="text-xs text-slate-500">{stat?.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-12 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Icon name="Search" size={15} className="absolute left-12 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search member name..."
            value={search}
            onChange={e => setSearch(e?.target?.value)}
            className="w-full pl-36 pr-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          />
        </div>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e?.target?.value)}
          className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 bg-white"
        >
          <option value="all">All Statuses</option>
          <option value="completed">Completed</option>
          <option value="attended">Attended</option>
          <option value="enrolled">Enrolled</option>
          <option value="absent">Absent</option>
        </select>
      </div>

      {/* Members Table */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
        </div>
      ) : filtered?.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200/60 p-32 text-center">
          <Icon name="Users" size={36} className="text-slate-300 mx-auto mb-12" />
          <p className="text-slate-500 text-sm">No member outcomes found</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-20 py-12">Member</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-12 py-12">Program</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-12 py-12">Status</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-12 py-12">Pre Score</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-12 py-12">Post Score</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-12 py-12">Improvement</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-12 py-12">Hours</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-12 py-12">Certification</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered?.map(o => (
                  <tr key={o?.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-20 py-14">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{o?.member_name}</p>
                        <p className="text-xs text-slate-400">{o?.member_role} · {o?.member_department}</p>
                      </div>
                    </td>
                    <td className="px-12 py-14">
                      <div>
                        <p className="text-xs font-semibold text-slate-700 max-w-[140px] truncate">{o?.program_name || o?.training_programs?.program_name}</p>
                        <p className="text-xs text-slate-400">{o?.delivering_partner || o?.training_programs?.partners?.brand_name}</p>
                      </div>
                    </td>
                    <td className="px-12 py-14"><AttendanceBadge status={o?.attendance_status} /></td>
                    <td className="px-12 py-14">
                      <span className="text-sm font-semibold text-slate-600">{o?.pre_training_score ?? '—'}</span>
                    </td>
                    <td className="px-12 py-14">
                      <span className="text-sm font-semibold text-slate-900">{o?.post_training_score ?? '—'}</span>
                    </td>
                    <td className="px-12 py-14">
                      {o?.score_improvement != null ? (
                        <span className={`text-sm font-bold px-8 py-3 rounded-lg ${o?.score_improvement > 0 ? 'bg-emerald-100 text-emerald-700' : o?.score_improvement < 0 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>
                          {o?.score_improvement > 0 ? '+' : ''}{o?.score_improvement}
                        </span>
                      ) : <span className="text-slate-300 text-sm">—</span>}
                    </td>
                    <td className="px-12 py-14">
                      <span className="text-sm text-slate-700">{o?.training_hours || 0}h</span>
                    </td>
                    <td className="px-12 py-14">
                      {o?.certification_earned ? (
                        <div className="flex items-center gap-6">
                          <Icon name="Award" size={13} className="text-amber-500 flex-shrink-0" />
                          <span className="text-xs font-semibold text-amber-700 max-w-[120px] truncate">{o?.certification_earned}</span>
                        </div>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Outcomes Component ─────────────────────────────────

const EnablementOutcomes = () => {
  const toast = useToast();
  const [activeSubTab, setActiveSubTab] = useState('partner-outcomes');
  const [partners, setPartners] = useState([]);
  const [targets, setTargets] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [memberOutcomes, setMemberOutcomes] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadInitialData(); }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [pts, tgts, revs] = await Promise.all([
        partnerService?.getAll(),
        outcomeTargetsService?.getAll(new Date()?.getFullYear()),
        quarterlyReviewsService?.getAll()
      ]);
      setPartners(pts || []);
      setTargets(tgts || []);
      setReviews(revs || []);
    } catch (err) {
      toast?.error?.('Failed to load outcomes data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeSubTab === 'member-outcomes') loadMemberOutcomes();
  }, [activeSubTab]);

  const loadMemberOutcomes = async () => {
    setLoadingMembers(true);
    try {
      const data = await ecityMemberOutcomesService?.getAll();
      setMemberOutcomes(data || []);
    } catch (err) {
      toast?.error?.('Failed to load member outcomes');
    } finally {
      setLoadingMembers(false);
    }
  };

  const subTabs = [
    { id: 'partner-outcomes', label: 'Partner Outcomes', icon: 'BarChart3' },
    { id: 'targets', label: 'Outcome Targets', icon: 'Target' },
    { id: 'quarterly-reviews', label: 'Quarterly Reviews', icon: 'CalendarCheck' },
    { id: 'member-outcomes', label: 'Member Outcomes', icon: 'Users' },
  ];

  return (
    <div className="space-y-20">
      {/* Section Header */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-24">
        <div className="flex items-center gap-14">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/25">
            <Icon name="TrendingUp" size={22} className="text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Enablement Outcomes</h2>
            <p className="text-sm text-slate-500 mt-1">
              Track the real impact of enablement — capability improvements, certification gains, training hours, and deal performance correlation
            </p>
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="flex gap-8 mt-20 overflow-x-auto pb-1">
          {subTabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveSubTab(tab?.id)}
              className={`flex items-center gap-8 px-16 py-10 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${
                activeSubTab === tab?.id
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-md shadow-emerald-500/20'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Icon name={tab?.icon} size={15} />
              {tab?.label}
            </button>
          ))}
        </div>
      </div>

      {/* Sub-tab Content */}
      {loading ? (
        <div className="flex items-center justify-center py-32">
          <div className="w-10 h-10 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {activeSubTab === 'partner-outcomes' && <PartnerOutcomesSection partners={partners} />}
          {activeSubTab === 'targets' && <OutcomeTargetsSection targets={targets} />}
          {activeSubTab === 'quarterly-reviews' && <QuarterlyReviewCards reviews={reviews} partners={partners} />}
          {activeSubTab === 'member-outcomes' && (
            <MemberOutcomesSection outcomes={memberOutcomes} loading={loadingMembers} />
          )}
        </>
      )}
    </div>
  );
};

export default EnablementOutcomes;
